import {Alert, AlertDescription, AlertIcon, AlertTitle, Box, Code} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {HTTPNotFound} from 'pwa-kit-react-sdk/ssr/universal/errors'
import React, {useEffect} from 'react'
import {useCommerceAPI} from '../../commerce-api/contexts'
import useAsync from '../../commerce-api/hooks/useAsync'
import AddToCartCTA, {Skeleton as AddToCartSkeleton} from '../../components/add-to-cart-cta'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import {BonusProductsModalProvider} from '../../hooks/use-bonus-products-modal'

/**
 * Display add to cart button for a given product ID.
 *
 * @param {string} pid - Product ID
 * @param {string} label - Label for the button
 *
 * @example
 *   <pwa-widget-add-to-cart pid="101001" label="add to cart">
 *     $include(...)$
 *   </pwa-widget-add-to-cart>
 */
const WidgetAddToCart = ({pid, label}) => {
    const api = useCommerceAPI()

    const {
        loading,
        error,
        data: product,
        execute
    } = useAsync(async (id) => {
        const product = await api.shopperProducts.getProduct({parameters: {id, allImages: true}})
        // Copied from ProductDetail.getProps
        // The `commerce-isomorphic-sdk` package does not throw errors, so
        // we have to check the returned object type to inconsistencies.
        if (typeof product?.type === 'string') {
            throw new HTTPNotFound(product.detail)
        }
        return product
    })

    useEffect(() => {
        if (pid) {
            execute(pid)
        }
    }, [execute, pid])

    if (!pid) {
        return <Alert status={'error'}>No product ID provided</Alert>
    }

    if (loading) {
        return <AddToCartSkeleton />
    }

    if (error) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Box>
                    <AlertTitle>
                        Something bad happened while loading <Code>{pid}</Code>!
                    </AlertTitle>
                    <AlertDescription>{error.toString()}</AlertDescription>
                </Box>
            </Alert>
        )
    }

    // prevent initial flash of undefined product
    if (!product) {
        return null
    }

    return (
        <AddToCartModalProvider>
            <BonusProductsModalProvider>
                <AddToCartCTA label={label} product={product} scrollToTop={false} />
            </BonusProductsModalProvider>
        </AddToCartModalProvider>
    )
}

WidgetAddToCart.propTypes = {
    pid: PropTypes.string,
    label: PropTypes.string
}

export default WidgetAddToCart
